import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  AppBar,
  Toolbar,
  Rating,
  SelectChangeEvent,
  Pagination
} from '@mui/material';
import {
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  Message as MessageIcon,
  DirectionsCar as CarIcon,
  WhatsApp as WhatsAppIcon
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface InstructorSearchResult {
  id: number;
  userId: string;
  baseHourlyRate: number;
  transmissionType: string;
  bio: string | null;
  firstName: string;
  lastName: string;
  profileImageUrl: string | null;
  isFeatured: boolean;
  email: string;
  licenseTypes: string | string[] | null;
  vehicleImageUrls: string | string[] | null;
  averageRating: number;
  reviewCount: number;
  verifiedPassCount: number;
  logoUrl: string | null;
  phoneNumber: string | null;
  hasWhatsApp: boolean;
  gender?: string | null;
  isClaimed?: boolean;
}

const LICENSE_CATEGORIES = {
  B: 'Category B (Car)',
  A: 'Category A (Motorcycle)',
  C: 'Category C (Lorry)',
  D: 'Category D (Bus)',
  BE: 'Category B+E (Trailer)'
};

const getImageUrl = (path: string | null) => {
  return api.getImageUrl(path);
};

const getVehicleImages = (urls: any): string[] => {
  if (!urls) return [];
  let rawUrls: string[] = [];
  if (typeof urls === 'string') {
    rawUrls = urls.split(';').filter(Boolean);
  } else if (Array.isArray(urls)) {
    rawUrls = urls;
  }
  return rawUrls.map(url => getImageUrl(url));
};

const getLicenseChips = (licenseTypes: string | string[] | null) => {
  let types: string[] = [];
  if (!licenseTypes) {
    types = ['B'];
  } else if (typeof licenseTypes === 'string') {
    types = licenseTypes.split(',').map(s => s.trim()).filter(Boolean);
  } else if (Array.isArray(licenseTypes)) {
    types = licenseTypes;
  }
  
  return types.map(t => {
    const label = LICENSE_CATEGORIES[t as keyof typeof LICENSE_CATEGORIES] || `Cat. ${t}`;
    return (
      <Chip
        key={t}
        label={label}
        size="small"
        color="info"
        variant="outlined"
        sx={{ fontWeight: 'bold', mr: 0.5, mb: 0.5 }}
      />
    );
  });
};

const VehicleGallery: React.FC<{ imageUrls: string | string[] | null }> = ({ imageUrls }) => {
  const images = getVehicleImages(imageUrls);
  const [activeIdx, setActiveIdx] = useState(0);

  if (images.length === 0) {
    return (
      <Box 
        sx={{ 
          width: '100%', 
          height: 200, 
          background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: '1px solid #eef2f6',
          color: '#1976d2',
          gap: 1
        }}
      >
        <CarIcon sx={{ fontSize: 48, opacity: 0.8 }} />
        <Typography variant="caption" sx={{ fontWeight: 'bold', letterSpacing: 0.5, opacity: 0.7 }}>
          No Vehicle Image Uploaded
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', borderRadius: '16px 16px 0 0', overflow: 'hidden' }}>
      <Box 
        component="img" 
        src={images[activeIdx]} 
        alt="Instructor Vehicle"
        sx={{ 
          width: '100%', 
          height: 200, 
          objectFit: 'cover',
          borderBottom: '1px solid #eef2f6',
          display: 'block'
        }}
      />
      {images.length > 1 && (
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: 1, 
            position: 'absolute', 
            bottom: 12, 
            left: 0, 
            right: 0,
            bgcolor: 'rgba(0,0,0,0.4)',
            backdropFilter: 'blur(4px)',
            py: 0.8,
            width: 'fit-content',
            mx: 'auto',
            px: 2,
            borderRadius: 5
          }}
        >
          {images.map((_, idx) => (
            <Box
              key={idx}
              onClick={() => setActiveIdx(idx)}
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: activeIdx === idx ? 'primary.main' : 'rgba(255,255,255,0.6)',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'scale(1.2)'
                }
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export const REGION_GROUPS = [
  {
    name: 'London',
    outcodes: [
      { code: 'BARKING AND DAGENHAM', area: 'Barking and Dagenham, London' },
      { code: 'BARNET', area: 'Barnet, London' },
      { code: 'BEXLEY', area: 'Bexley, London' },
      { code: 'BRENT', area: 'Brent, London' },
      { code: 'BROMLEY', area: 'Bromley, London' },
      { code: 'CAMDEN', area: 'Camden, London' },
      { code: 'CITY OF LONDON', area: 'City of London, London' },
      { code: 'CROYDON', area: 'Croydon, London' },
      { code: 'EALING', area: 'Ealing, London' },
      { code: 'ENFIELD', area: 'Enfield, London' },
      { code: 'GREENWICH', area: 'Greenwich, London' },
      { code: 'HACKNEY', area: 'Hackney, London' },
      { code: 'HAMMERSMITH AND FULHAM', area: 'Hammersmith and Fulham, London' },
      { code: 'HARINGEY', area: 'Haringey, London' },
      { code: 'HARROW', area: 'Harrow, London' },
      { code: 'HAVERING', area: 'Havering, London' },
      { code: 'HILLINGDON', area: 'Hillingdon, London' },
      { code: 'HOUNSLOW', area: 'Hounslow, London' },
      { code: 'ISLINGTON', area: 'Islington, London' },
      { code: 'KENSINGTON AND CHELSEA', area: 'Kensington and Chelsea, London' },
      { code: 'KINGSTON UPON THAMES', area: 'Kingston upon Thames, London' },
      { code: 'LAMBETH', area: 'Lambeth, London' },
      { code: 'LEWISHAM', area: 'Lewisham, London' },
      { code: 'MERTON', area: 'Merton, London' },
      { code: 'NEWHAM', area: 'Newham, London' },
      { code: 'REDBRIDGE', area: 'Redbridge, London' },
      { code: 'RICHMOND UPON THAMES', area: 'Richmond upon Thames, London' },
      { code: 'SOUTHWARK', area: 'Southwark, London' },
      { code: 'SUTTON', area: 'Sutton, London' },
      { code: 'TOWER HAMLETS', area: 'Tower Hamlets, London' },
      { code: 'WALTHAM FOREST', area: 'Waltham Forest, London' },
      { code: 'WANDSWORTH', area: 'Wandsworth, London' },
      { code: 'WESTMINSTER', area: 'Westminster, London' }
    ]
  },
  {
    name: 'Manchester',
    outcodes: [
      { code: 'MANCHESTER CITY CENTRE', area: 'Manchester City Centre, Manchester' },
      { code: 'SALFORD', area: 'Salford, Manchester' },
      { code: 'TRAFFORD', area: 'Trafford, Manchester' },
      { code: 'STOCKPORT', area: 'Stockport, Manchester' },
      { code: 'TAMESIDE', area: 'Tameside, Manchester' },
      { code: 'OLDHAM', area: 'Oldham, Manchester' },
      { code: 'ROCHDALE', area: 'Rochdale, Manchester' },
      { code: 'BURY', area: 'Bury, Manchester' },
      { code: 'BOLTON', area: 'Bolton, Manchester' },
      { code: 'WIGAN', area: 'Wigan, Manchester' }
    ]
  },
  {
    name: 'Birmingham',
    outcodes: [
      { code: 'BIRMINGHAM CITY CENTRE', area: 'Birmingham City Centre, Birmingham' },
      { code: 'SOLIHULL', area: 'Solihull, Birmingham' },
      { code: 'SUTTON COLDFIELD', area: 'Sutton Coldfield, Birmingham' },
      { code: 'EDGBASTON', area: 'Edgbaston, Birmingham' },
      { code: 'HARBORNE', area: 'Harborne, Birmingham' },
      { code: 'DUDLEY', area: 'Dudley, Birmingham' },
      { code: 'SANDWELL', area: 'Sandwell, Birmingham' },
      { code: 'WALSALL', area: 'Walsall, Birmingham' },
      { code: 'WOLVERHAMPTON', area: 'Wolverhampton, Birmingham' },
      { code: 'COVENTRY', area: 'Coventry, Birmingham' }
    ]
  },
  {
    name: 'Liverpool',
    outcodes: [
      { code: 'LIVERPOOL CITY CENTRE', area: 'Liverpool City Centre, Liverpool' },
      { code: 'KNOWSLEY', area: 'Knowsley, Liverpool' },
      { code: 'ST HELENS', area: 'St Helens, Liverpool' },
      { code: 'SEFTON', area: 'Sefton, Liverpool' },
      { code: 'WIRRAL', area: 'Wirral, Liverpool' }
    ]
  },
  {
    name: 'Leeds',
    outcodes: [
      { code: 'LEEDS CITY CENTRE', area: 'Leeds City Centre, Leeds' },
      { code: 'HEADINGLEY', area: 'Headingley, Leeds' },
      { code: 'HORSFORTH', area: 'Horsforth, Leeds' },
      { code: 'WETHERBY', area: 'Wetherby, Leeds' },
      { code: 'PUDSEY', area: 'Pudsey, Leeds' },
      { code: 'BRADFORD', area: 'Bradford, Leeds' },
      { code: 'WAKEFIELD', area: 'Wakefield, Leeds' }
    ]
  },
  {
    name: 'Bristol',
    outcodes: [
      { code: 'BRISTOL CITY CENTRE', area: 'Bristol City Centre, Bristol' },
      { code: 'CLIFTON', area: 'Clifton, Bristol' },
      { code: 'REDLAND', area: 'Redland, Bristol' },
      { code: 'BEDMINSTER', area: 'Bedminster, Bristol' },
      { code: 'BATH', area: 'Bath, Bristol' }
    ]
  },
  {
    name: 'Sheffield',
    outcodes: [
      { code: 'SHEFFIELD CITY CENTRE', area: 'Sheffield City Centre, Sheffield' },
      { code: 'KELHAM ISLAND', area: 'Kelham Island, Sheffield' },
      { code: 'ECCLESALL', area: 'Ecclesall, Sheffield' },
      { code: 'HILLSBOROUGH', area: 'Hillsborough, Sheffield' },
      { code: 'ROTHERHAM', area: 'Rotherham, Sheffield' }
    ]
  },
  {
    name: 'Newcastle',
    outcodes: [
      { code: 'NEWCASTLE CITY CENTRE', area: 'Newcastle City Centre, Newcastle' },
      { code: 'JESMOND', area: 'Jesmond, Newcastle' },
      { code: 'GOSFORTH', area: 'Gosforth, Newcastle' },
      { code: 'SUNDERLAND', area: 'Sunderland, Newcastle' },
      { code: 'GATESHEAD', area: 'Gateshead, Newcastle' }
    ]
  },
  {
    name: 'Nottingham',
    outcodes: [
      { code: 'NOTTINGHAM CITY CENTRE', area: 'Nottingham City Centre, Nottingham' },
      { code: 'WEST BRIDGFORD', area: 'West Bridgford, Nottingham' },
      { code: 'BEESTON', area: 'Beeston, Nottingham' },
      { code: 'DERBY', area: 'Derby, Nottingham' }
    ]
  },
  {
    name: 'Leicester',
    outcodes: [
      { code: 'LEICESTER CITY CENTRE', area: 'Leicester City Centre, Leicester' },
      { code: 'STONEYGATE', area: 'Stoneygate, Leicester' },
      { code: 'OADBY', area: 'Oadby, Leicester' },
      { code: 'LOUGHBOROUGH', area: 'Loughborough, Leicester' }
    ]
  },
  {
    name: 'Southampton',
    outcodes: [
      { code: 'SOUTHAMPTON CITY CENTRE', area: 'Southampton City Centre, Southampton' },
      { code: 'PORTSMOUTH CITY CENTRE', area: 'Portsmouth City Centre, Southampton' },
      { code: 'EASTLEIGH', area: 'Eastleigh, Southampton' },
      { code: 'FAREHAM', area: 'Fareham, Southampton' }
    ]
  },
  {
    name: 'Brighton',
    outcodes: [
      { code: 'BRIGHTON CITY CENTRE', area: 'Brighton City Centre, Brighton' },
      { code: 'HOVE', area: 'Hove, Brighton' },
      { code: 'WORTHING', area: 'Worthing, Brighton' }
    ]
  },
  {
    name: 'Oxford',
    outcodes: [
      { code: 'OXFORD CITY CENTRE', area: 'Oxford City Centre, Oxford' }
    ]
  },
  {
    name: 'Cambridge',
    outcodes: [
      { code: 'CAMBRIDGE CITY CENTRE', area: 'Cambridge City Centre, Cambridge' },
      { code: 'PETERBOROUGH', area: 'Peterborough, Cambridge' }
    ]
  },
  {
    name: 'Edinburgh',
    outcodes: [
      { code: 'EDINBURGH CITY CENTRE', area: 'Edinburgh City Centre, Edinburgh' },
      { code: 'ABERDEEN', area: 'Aberdeen, Edinburgh' },
      { code: 'DUNDEE', area: 'Dundee, Edinburgh' }
    ]
  },
  {
    name: 'Glasgow',
    outcodes: [
      { code: 'GLASGOW CITY CENTRE', area: 'Glasgow City Centre, Glasgow' }
    ]
  },
  {
    name: 'Cardiff',
    outcodes: [
      { code: 'CARDIFF CITY CENTRE', area: 'Cardiff City Centre, Cardiff' },
      { code: 'SWANSEA CITY CENTRE', area: 'Swansea City Centre, Cardiff' },
      { code: 'NEWPORT', area: 'Newport, Cardiff' }
    ]
  },
  {
    name: 'Belfast',
    outcodes: [
      { code: 'BELFAST CITY CENTRE', area: 'Belfast City Centre, Belfast' },
      { code: 'LONDONDERRY', area: 'Londonderry, Belfast' }
    ]
  }
];

export const SearchInstructors: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  
  // Search parameters
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [transmission, setTransmission] = useState('all');
  const [maxRate, setMaxRate] = useState<number>(50);
  const [licenseType, setLicenseType] = useState('all');
  const [gender, setGender] = useState('all');
  
  // Results & Loading
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [instructors, setInstructors] = useState<InstructorSearchResult[]>([]);
  
  // Pagination State
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);
  
  // Quote dialog state
  const [openQuoteDialog, setOpenQuoteDialog] = useState(false);
  const [selectedInstructor, setSelectedInstructor] = useState<InstructorSearchResult | null>(null);
  const [quoteMessage, setQuoteMessage] = useState('');
  const [prefTransmission, setPrefTransmission] = useState('Manual');
  const [experienceLevel, setExperienceLevel] = useState('Beginner');
  const [submittingQuote, setSubmittingQuote] = useState(false);
  const [quoteSuccess, setQuoteSuccess] = useState(false);

  const [myQuotes, setMyQuotes] = useState<any[]>([]);

  const fetchMyQuotes = async () => {
    if (isAuthenticated && user?.role === 'Student') {
      try {
        const res = await api.get('/Quotes/my-requests');
        setMyQuotes(res.data || []);
      } catch (err) {
        console.error('Error fetching sent quotes:', err);
      }
    }
  };

  useEffect(() => {
    fetchMyQuotes();
  }, [isAuthenticated, user]);

  useEffect(() => {
    const paramCity = searchParams.get('city') || '';
    const paramDistrict = searchParams.get('district') || '';
    const paramTransmission = searchParams.get('transmission') || 'all';
    const paramLicenseType = searchParams.get('licenseType') || 'all';
    const paramGender = searchParams.get('gender') || 'all';

    if (paramCity) setSelectedCity(paramCity);
    if (paramDistrict) {
      setSelectedDistrict(paramDistrict);
      
      const executeSearch = async () => {
        try {
          setLoading(true);
          setSearched(true);
          const res = await api.searchInstructors(
            paramDistrict,
            paramTransmission === 'all' ? undefined : paramTransmission,
            50, // default maxRate
            paramLicenseType === 'all' ? undefined : paramLicenseType,
            paramGender === 'all' ? undefined : paramGender,
            1,
            6
          );
          
          if (res.data && res.data.items !== undefined) {
            setInstructors(res.data.items || []);
            setTotalPages(res.data.totalPages || 1);
            setTotalItems(res.data.totalItems || 0);
          } else {
            setInstructors(res.data || []);
            setTotalPages(1);
            setTotalItems(res.data ? res.data.length : 0);
          }
          setPage(1);
        } catch (err) {
          console.error('Initial query search failed:', err);
        } finally {
          setLoading(false);
        }
      };
      executeSearch();
    }
    if (paramTransmission) setTransmission(paramTransmission);
    if (paramLicenseType) setLicenseType(paramLicenseType);
    if (paramGender) setGender(paramGender);
  }, [searchParams]);

  const fetchInstructors = async (pageNumber: number) => {
    if (!selectedDistrict) return;
    
    try {
      setLoading(true);
      setSearched(true);
      const res = await api.searchInstructors(
        selectedDistrict,
        transmission === 'all' ? undefined : transmission,
        maxRate,
        licenseType === 'all' ? undefined : licenseType,
        gender === 'all' ? undefined : gender,
        pageNumber,
        6 // Page size is 6
      );
      
      if (res.data && res.data.items !== undefined) {
        setInstructors(res.data.items || []);
        setTotalPages(res.data.totalPages || 1);
        setTotalItems(res.data.totalItems || 0);
      } else {
        // Fallback for old/other format
        setInstructors(res.data || []);
        setTotalPages(1);
        setTotalItems(res.data ? res.data.length : 0);
      }
      setPage(pageNumber);
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetchInstructors(1);
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    fetchInstructors(value);
    // Smooth scroll to near the top of the search results
    window.scrollTo({ top: 350, behavior: 'smooth' });
  };

  const handleOpenQuote = (instructor: InstructorSearchResult) => {
    setSelectedInstructor(instructor);
    setPrefTransmission(instructor.transmissionType === 'Both' ? 'Manual' : instructor.transmissionType);
    setOpenQuoteDialog(true);
    setQuoteSuccess(false);
    setQuoteMessage('');
  };

  const handleSendQuote = async () => {
    if (!selectedInstructor) return;
    
    try {
      setSubmittingQuote(true);
      await api.requestQuote({
        instructorId: selectedInstructor.id,
        message: quoteMessage,
        preferredTransmission: prefTransmission,
        experienceLevel: experienceLevel
      });
      setQuoteSuccess(true);
      fetchMyQuotes();
    } catch (err) {
      console.error('Failed to submit quote:', err);
    } finally {
      setSubmittingQuote(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f4f6f9' }}>
      {/* Sticky Navigation Header */}
      <AppBar position="sticky" sx={{ background: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(10px)', boxShadow: '0 2px 12px rgba(0, 0, 0, 0.05)', color: '#1a237e' }}>
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', px: 2 }}>
          <Box display="flex" alignItems="center" gap={1} onClick={() => navigate('/')} sx={{ cursor: 'pointer' }}>
            <CarIcon sx={{ color: '#1967D2', fontSize: 28 }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1a237e', letterSpacing: '-0.5px', fontFamily: '"Outfit", sans-serif' }}>
              adi<span style={{ color: '#E53935', fontWeight: '900' }}>L</span>essons
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/')}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
          >
            Back to Home
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 6, flexGrow: 1 }}>
        {/* Search Panel Header */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h4" fontWeight="bold" color="primary" gutterBottom>
            Find Driving Instructors in Your Area
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Select your City and District to find matching certified instructors in your area.
          </Typography>
        </Box>

        {/* Search & Filter Form */}
        <Card sx={{ p: 4, borderRadius: 4, border: '1px solid #e0e0e0', mb: 5, boxShadow: '0 4px 20px rgba(0,0,0,0.02)', maxWidth: 'md', mx: 'auto' }}>
          <Box component="form" onSubmit={handleSearchSubmit}>
            <Grid container spacing={3} alignItems="center">
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControl fullWidth required>
                  <InputLabel id="city-select-label">City</InputLabel>
                  <Select
                    labelId="city-select-label"
                    value={selectedCity}
                    label="City"
                    onChange={(e: SelectChangeEvent) => {
                      setSelectedCity(e.target.value);
                      setSelectedDistrict('');
                    }}
                  >
                    {REGION_GROUPS.map((r) => (
                      <MenuItem key={r.name} value={r.name}>
                        {r.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControl fullWidth required disabled={!selectedCity}>
                  <InputLabel id="district-select-label">District</InputLabel>
                  <Select
                    labelId="district-select-label"
                    value={selectedDistrict}
                    label="District"
                    onChange={(e: SelectChangeEvent) => setSelectedDistrict(e.target.value)}
                  >
                    {selectedCity && 
                      (REGION_GROUPS.find((r) => r.name === selectedCity)?.outcodes || []).map((o) => (
                        <MenuItem key={o.code} value={o.code}>
                          {o.area.split(',')[0]}
                        </MenuItem>
                      ))
                    }
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControl fullWidth>
                  <InputLabel id="transmission-select-label">Transmission</InputLabel>
                  <Select
                    labelId="transmission-select-label"
                    value={transmission}
                    label="Transmission"
                    onChange={(e: SelectChangeEvent) => setTransmission(e.target.value)}
                  >
                    <MenuItem value="all">Any Gearbox</MenuItem>
                    <MenuItem value="Manual">Manual</MenuItem>
                    <MenuItem value="Automatic">Automatic</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControl fullWidth>
                  <InputLabel id="licensetype-select-label">License Type</InputLabel>
                  <Select
                    labelId="licensetype-select-label"
                    value={licenseType}
                    label="License Type"
                    onChange={(e: SelectChangeEvent) => setLicenseType(e.target.value)}
                  >
                    <MenuItem value="all">Any License</MenuItem>
                    <MenuItem value="B">Car (Category B)</MenuItem>
                    <MenuItem value="A">Motorcycle (Category A)</MenuItem>
                    <MenuItem value="C">Lorry (Category C)</MenuItem>
                    <MenuItem value="D">Bus (Category D)</MenuItem>
                    <MenuItem value="BE">Car & Trailer (Category B+E)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControl fullWidth>
                  <InputLabel id="gender-select-label">Gender</InputLabel>
                  <Select
                    labelId="gender-select-label"
                    value={gender}
                    label="Gender"
                    onChange={(e: SelectChangeEvent) => setGender(e.target.value)}
                  >
                    <MenuItem value="all">Any Gender</MenuItem>
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Box sx={{ px: 2 }}>
                  <Typography id="price-slider-label" variant="caption" color="text.secondary" gutterBottom>
                    Max Rate: £{maxRate}
                  </Typography>
                  <Slider
                    value={maxRate}
                    min={20}
                    max={60}
                    step={1}
                    onChange={(_, val) => setMaxRate(val as number)}
                    valueLabelDisplay="auto"
                    aria-labelledby="price-slider-label"
                  />
                </Box>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={<SearchIcon />}
                  sx={{ py: 1.8, borderRadius: 2.5, fontWeight: 'bold' }}
                >
                  Search
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Card>

        {/* Search Results */}
        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress size={48} />
          </Box>
        ) : searched ? (
          <Box>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
              Found {totalItems} Instructors
            </Typography>
            
            {instructors.length > 0 ? (
              <>
                <Grid container spacing={4}>
                {instructors.map((instructor) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={instructor.id}>
                    <Card
                      sx={{
                        borderRadius: 4,
                        border: instructor.isFeatured ? '2px solid #ed6c02' : '1px solid #eef2f6',
                        boxShadow: instructor.isFeatured ? '0 10px 24px rgba(237, 108, 2, 0.12)' : '0 4px 15px rgba(0, 0, 0, 0.02)',
                        transition: 'transform 0.2s',
                        '&:hover': {
                          transform: 'translateY(-4px)'
                        },
                        position: 'relative'
                      }}
                    >
                      {instructor.isFeatured && (
                        <Chip
                          label="Featured ADI"
                          color="warning"
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 16,
                            right: 16,
                            fontWeight: 'bold',
                            fontSize: '0.7rem',
                            zIndex: 2
                          }}
                        />
                      )}
                      {instructor.isClaimed === false && (
                        <Chip
                          label="Unclaimed Profile"
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 16,
                            left: 16,
                            fontWeight: 'bold',
                            fontSize: '0.7rem',
                            zIndex: 2,
                            backgroundColor: '#475569',
                            color: '#ffffff'
                          }}
                        />
                      )}
                      
                      <VehicleGallery imageUrls={instructor.vehicleImageUrls} />
                      
                      <CardContent sx={{ p: 4, pt: getVehicleImages(instructor.vehicleImageUrls).length > 0 ? 2.5 : 4 }}>
                        <Box display="flex" gap={2} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                          <Box display="flex" gap={2} alignItems="center">
                            <Avatar
                              src={instructor.profileImageUrl ? getImageUrl(instructor.profileImageUrl) : undefined}
                              onClick={() => navigate(`/public/instructor/${instructor.id}`)}
                              sx={{ width: 64, height: 64, bgcolor: '#1976d2', fontSize: 24, cursor: 'pointer', '&:hover': { opacity: 0.85 } }}
                            >
                              {instructor.firstName[0]}
                            </Avatar>
                            <Box>
                              <Typography 
                                variant="h6" 
                                fontWeight="bold" 
                                color="text.primary"
                                onClick={() => navigate(`/public/instructor/${instructor.id}`)}
                                sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main', textDecoration: 'underline' } }}
                              >
                                {instructor.firstName} {instructor.lastName}
                              </Typography>
                              <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                                <Rating value={instructor.averageRating || 5} precision={0.1} readOnly size="small" />
                                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                                  {instructor.averageRating ? instructor.averageRating.toFixed(1) : '5.0'} ({instructor.reviewCount || 0} reviews)
                                </Typography>
                                {instructor.verifiedPassCount > 0 && (
                                  <Chip
                                    label={`${instructor.verifiedPassCount} Verified Pass${instructor.verifiedPassCount > 1 ? 'es' : ''}`}
                                    color="success"
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontWeight: 'black', fontSize: '0.65rem', height: 20, ml: 1 }}
                                  />
                                )}
                              </Box>
                            </Box>
                          </Box>
                          {instructor.logoUrl && (
                            <Avatar
                              variant="rounded"
                              src={getImageUrl(instructor.logoUrl)}
                              sx={{ width: 48, height: 48, bgcolor: '#fff', border: '1px solid #eef2f6', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', p: 0.5 }}
                            />
                          )}
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ minHeight: 60, mb: 2, lineHeight: 1.6 }}>
                          {instructor.bio || "Hello, I am a certified DVSA Approved Driving Instructor. I focus on safe, defensive driving techniques to ensure my students pass their practical test on the first try."}
                        </Typography>

                        {instructor.isClaimed === false && (
                          <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, backgroundColor: 'rgba(25, 103, 210, 0.04)', border: '1px dashed rgba(25, 103, 210, 0.2)' }}>
                            <Typography variant="caption" color="primary.main" fontWeight="600" display="block">
                              Are you this instructor?
                            </Typography>
                            <Typography 
                              variant="caption" 
                              color="text.secondary" 
                              sx={{ cursor: 'pointer', textDecoration: 'underline', '&:hover': { color: 'primary.main' } }}
                              onClick={() => navigate('/claim-profile')}
                            >
                              Click here to claim this profile
                            </Typography>
                          </Box>
                        )}

                        {/* License Categories */}
                        <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {getLicenseChips(instructor.licenseTypes)}
                        </Box>

                        <Box display="flex" flexWrap="wrap" gap={1.5} justifyContent="space-between" alignItems="center">
                          <Box display="flex" gap={1.5} flexWrap="wrap">
                            <Chip label={`£${instructor.baseHourlyRate}/hr`} color="primary" variant="outlined" sx={{ fontWeight: 'bold' }} />
                            <Chip label={instructor.transmissionType} color="secondary" variant="outlined" sx={{ fontWeight: 'bold' }} />
                            {instructor.gender && (
                              <Chip label={instructor.gender} color="default" variant="outlined" sx={{ fontWeight: 'bold' }} />
                            )}
                          </Box>
                          <Box display="flex" gap={1.5} flexWrap="wrap">
                            {instructor.hasWhatsApp && instructor.phoneNumber && (
                              <Button
                                variant="contained"
                                color="success"
                                startIcon={<WhatsAppIcon />}
                                href={`https://wa.me/${instructor.phoneNumber.replace(/[^0-9]/g, '').startsWith('0') ? '44' + instructor.phoneNumber.replace(/[^0-9]/g, '').substring(1) : instructor.phoneNumber.replace(/[^0-9]/g, '')}?text=Hello%20${instructor.firstName},%20I%20found%20your%20profile%20on%20adiLessons%20and%20would%20like%20to%20inquire%20about%20lessons.`}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ 
                                  borderRadius: 2, 
                                  textTransform: 'none', 
                                  fontWeight: 'bold',
                                  backgroundColor: '#2e7d32',
                                  '&:hover': {
                                    backgroundColor: '#1b5e20'
                                  }
                                }}
                              >
                                Message WhatsApp
                              </Button>
                            )}
                            {isAuthenticated && user?.role === 'Student' && myQuotes.some(q => q.instructorId === instructor.id && q.status === 'Pending') ? (
                              <Button
                                variant="outlined"
                                color="warning"
                                disabled
                                startIcon={<MessageIcon />}
                                sx={{ 
                                  borderRadius: 2, 
                                  textTransform: 'none', 
                                  fontWeight: 'bold', 
                                  '&.Mui-disabled': { 
                                    color: 'warning.main', 
                                    borderColor: 'warning.main' 
                                  } 
                                }}
                              >
                                Request Pending
                              </Button>
                            ) : (
                              <Button
                                variant="contained"
                                color="primary"
                                startIcon={<MessageIcon />}
                                onClick={() => handleOpenQuote(instructor)}
                                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
                              >
                                Send Request
                              </Button>
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6, mb: 2 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    size="large"
                    sx={{
                      '& .MuiPaginationItem-root': {
                        fontWeight: 'bold',
                        borderRadius: 2,
                        transition: 'all 0.2s',
                        '&:hover': {
                          transform: 'scale(1.1)',
                          bgcolor: 'primary.light',
                          color: 'white',
                        },
                        '&.Mui-selected': {
                          boxShadow: '0 4px 10px rgba(25, 118, 210, 0.25)',
                        }
                      }
                    }}
                  />
                </Box>
              )}
            </>
          ) : (
              <Box sx={{ py: 8, textAlign: 'center', bgcolor: '#fff', borderRadius: 4, border: '1px dashed #ccc' }}>
                <Typography variant="body1" color="text.secondary">
                  No instructors currently serve this district. Try selecting another city or district.
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          // Welcome Placeholder
          <Box sx={{ py: 8, textAlign: 'center', bgcolor: '#fff', borderRadius: 4, border: '1px dashed #ccc' }}>
            <Typography variant="h6" color="text.primary" gutterBottom>
              Begin Your Search
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Select a city and district above to view local Approved Driving Instructors.
            </Typography>
          </Box>
        )}
      </Container>

      {/* Quote Dialog */}
      <Dialog open={openQuoteDialog} onClose={() => setOpenQuoteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          Send a Request to {selectedInstructor?.firstName}
        </DialogTitle>
        <DialogContent dividers>
          {quoteSuccess ? (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <Typography variant="h6" color="success.main" fontWeight="bold" gutterBottom>
                Request Sent!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your request has been delivered to {selectedInstructor?.firstName}. They will receive a WhatsApp notification and will respond soon.
              </Typography>
            </Box>
          ) : !isAuthenticated ? (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary" paragraph>
                You must sign in or register a student account to send a request.
              </Typography>
              <Button variant="contained" onClick={() => navigate('/login')} sx={{ borderRadius: 2 }}>
                Sign In Now
              </Button>
            </Box>
          ) : (
            <Box display="flex" flexDirection="column" gap={3}>
              <FormControl fullWidth>
                <InputLabel id="quote-trans-select">Preferred Transmission</InputLabel>
                <Select
                  labelId="quote-trans-select"
                  value={prefTransmission}
                  label="Preferred Transmission"
                  onChange={(e) => setPrefTransmission(e.target.value)}
                >
                  <MenuItem value="Manual">Manual</MenuItem>
                  <MenuItem value="Automatic">Automatic</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel id="experience-select">Your Current Driving Experience</InputLabel>
                <Select
                  labelId="experience-select"
                  value={experienceLevel}
                  label="Your Current Driving Experience"
                  onChange={(e) => setExperienceLevel(e.target.value)}
                >
                  <MenuItem value="Beginner">Beginner (No prior experience)</MenuItem>
                  <MenuItem value="Intermediate">Intermediate (Some hours completed)</MenuItem>
                  <MenuItem value="Ready">Test Ready (Looking for mock tests/brush-ups)</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Your Message"
                placeholder="List your availability days, pickup location, or any questions you have..."
                value={quoteMessage}
                onChange={(e) => setQuoteMessage(e.target.value)}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setOpenQuoteDialog(false)} color="inherit" sx={{ fontWeight: 'bold' }}>
            {quoteSuccess ? 'Close' : 'Cancel'}
          </Button>
          {!quoteSuccess && isAuthenticated && (
            <Button
              variant="contained"
              onClick={handleSendQuote}
              disabled={submittingQuote}
              sx={{ fontWeight: 'bold' }}
            >
              {submittingQuote ? <CircularProgress size={24} /> : 'Send Request'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SearchInstructors;
